import styled from "@emotion/styled";
import { ColorProp, getColorFromTheme } from "styles/themes";

import { FeatureUsage } from "lib/hooks/api/usage/useCompanyFeatureUsage";
import { FEATURE_USAGE_CONFIG, formatDaysUsed, formatShare } from "./featureUsageConfig";

type Props = {
  /** Every feature, in the order to list them. */
  features: FeatureUsage[];
};

/** Each feature's days used and share of the total, keyed to the pie by colour. */
function FeatureUsageLegend({ features }: Props) {
  return (
    <Legend aria-label="Feature usage by feature">
      {features.map(({ entityType, daysUsed, share }) => {
        const { label, color } = FEATURE_USAGE_CONFIG[entityType];
        return (
          <Row key={entityType}>
            <Swatch swatchColor={color} />
            <Label>{label}</Label>
            <Figure>{formatDaysUsed(daysUsed)}</Figure>
            <Share>{formatShare(share)}</Share>
          </Row>
        );
      })}
    </Legend>
  );
}

const Legend = styled.ul({
  flex: 1,
  minWidth: 260,
  maxWidth: 480,
  margin: 0,
  padding: 0,
  listStyle: "none",
});

const Row = styled.li(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "12px minmax(0, 1fr) 88px 48px",
  alignItems: "center",
  gap: 12,
  padding: "10px 0",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

/** A feature's colour, as the pie, legend and table key it. */
export const Swatch = styled.span<{ swatchColor: ColorProp }>(({ theme, swatchColor }) => ({
  width: 12,
  height: 12,
  borderRadius: 2,
  backgroundColor: getColorFromTheme(swatchColor, theme),
}));

const Label = styled.span({
  fontWeight: 500,
});

const Figure = styled.span({
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
});

const Share = styled(Figure)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
}));

export default FeatureUsageLegend;
